import container from '../container/container';
import preloader from '../preloader/preloader';

export interface PublicVarsInterface {
	cssId: {
		itemCls: string,
		containerCls: string,
		isLoadingCls: string,
		outerWrapperCls: string,
		containerTouchId: string
		arrowLeftId: string,
		arrowRightId: string,
		isAnimatingCls: string
		loaderWrapperCls: string,
		imageElemCls: string
	},

	autoplaySpeed?: number,
	itemsMoveBy?: number,
	itemsSetDeviation: number,
	itemsCountTillReset: number,
	mouseMoveSupport: boolean,
}

interface PrivateVarsInterface {
	name: string,
	itemsCount: number,
	firstItem: number,
	lastItem: number,
	itemWidth: number,
	modifier: number,
	isAnimating: boolean,
	itemsVisible: number,
	items: Node[],
	itemsSetDeviation: number,
	itemsCountTillReset: number,
	itemsMoveBy: number | null,
	callbacks: {
		'beforeLoadEnd': (() => void) | null
	},
	imgPreload: Map<number, { path: string, preloaded: boolean }>,
	touchStart: number,
	touchDiff: number,
	touchStartY: number,
	touchDiffY: number,
	touchStarted: boolean,
	autoplayInterval: ReturnType<typeof setInterval> | null,
	autoplaySpeed: number | null
}

const sliderListCut = function (id: number) {
	let publicVars: PublicVarsInterface;

	const privateVars: PrivateVarsInterface = {
		name: 'slider-list-cut(' + id + ')',
		itemsCount: 0,
		firstItem: 0,
		lastItem: 0,
		itemWidth: 0,
		modifier: 0,
		isAnimating: true,
		itemsVisible: 1,
		items: [],
		itemsSetDeviation: 1,
		itemsCountTillReset: 0,
		itemsMoveBy: null,
		callbacks: {
			'beforeLoadEnd': null
		},
		imgPreload: new Map(),
		touchStart: 0,
		touchDiff: 0,
		touchStartY: 0,
		touchDiffY: 0,
		touchStarted: false,
		autoplayInterval: null,
		autoplaySpeed: null,
	};

	const methods = {
		init: (options: PublicVarsInterface) => {
			container.msg(privateVars.name, 'init');

			publicVars = options;

			preloader.init();

			methods.bind();

			methods.startup();

			methods.preloadImages();
		},

		bind: function () {
			let resizeRAF: number;

			if (publicVars.cssId.arrowLeftId) {
				const arrowLeftHtml = document.querySelector(publicVars.cssId.arrowLeftId) as HTMLElement;
				arrowLeftHtml?.addEventListener('click', function () {
					container.msg(privateVars.name, 'arrow left click');

					methods.actionMoveSlider('left');
				});
			}

			if (publicVars.cssId.arrowRightId) {
				const arrowRightHtml = document.querySelector(publicVars.cssId.arrowRightId) as HTMLElement;
				arrowRightHtml?.addEventListener('click', function () {
					container.msg(privateVars.name, 'arrow right click');

					methods.actionMoveSlider('right');
				});
			}

			window.addEventListener('resize', function () {
				cancelAnimationFrame(resizeRAF);

				resizeRAF = requestAnimationFrame(function () {
					methods.handleWindowResize();
				});
			});

			window.addEventListener('orientationchange', function () {
				cancelAnimationFrame(resizeRAF);

				resizeRAF = requestAnimationFrame(function () {
					methods.handleWindowResize();
				});
			});

			const containerTouchIdHtml = document.querySelector(publicVars.cssId.containerTouchId);

			containerTouchIdHtml?.addEventListener('touchstart', function (event: Event) {

				const touchTarget = event.target as HTMLElement;
				const touchEvent = event as TouchEvent;

				if ((publicVars.cssId.arrowLeftId && touchTarget.closest(publicVars.cssId.arrowLeftId)) ||
					(publicVars.cssId.arrowRightId && touchTarget.closest(publicVars.cssId.arrowRightId))) {
					// If it is, return early to prevent the rest of the code from executing
					return;
				}

				if (privateVars.touchStarted || privateVars.isAnimating) {
					return;
				}

				privateVars.touchStart = touchEvent.touches[0].clientX;
				privateVars.touchStartY = touchEvent.touches[0].clientY;

				privateVars.touchStarted = true;

				methods.clearAutoplayInterval();
			});

			containerTouchIdHtml?.addEventListener('touchmove', function (event: Event) {

				const touchEvent = event as TouchEvent;

				if (privateVars.touchStarted && !privateVars.isAnimating) {
					privateVars.touchDiff = touchEvent.touches[0].clientX - privateVars.touchStart;
					privateVars.touchDiffY = touchEvent.touches[0].clientY - privateVars.touchStartY;

					methods.handleTouchMove(privateVars.touchDiff, privateVars.touchDiffY);
				}
			});

			containerTouchIdHtml?.addEventListener('touchend', function () {
				methods.handleTouchEnd();
				methods.setAutoplayInterval();
			});

			if (window.matchMedia('(hover: hover) and (pointer: fine)').matches && publicVars.mouseMoveSupport) {
				containerTouchIdHtml?.addEventListener('mousedown', function (event: Event) {

					const mouseEvent = event as MouseEvent;

					event.preventDefault();

					const mouseTarget = event.target as HTMLElement;

					if ((publicVars.cssId.arrowLeftId && mouseTarget.closest(publicVars.cssId.arrowLeftId))
						|| (publicVars.cssId.arrowRightId && mouseTarget.closest(publicVars.cssId.arrowRightId))) {
						// If it is, return early to prevent the rest of the code from executing
						return;
					}

					if (privateVars.touchStarted || privateVars.isAnimating) {
						return;
					}

					privateVars.touchStart = mouseEvent.clientX;
					privateVars.touchStartY = mouseEvent.clientY;

					privateVars.touchStarted = true;

					methods.clearAutoplayInterval();
				});

				containerTouchIdHtml?.addEventListener('mousemove', function (event: Event) {
						event.preventDefault();

						const mouseEvent = event as MouseEvent;

						if (privateVars.touchStarted && !privateVars.isAnimating) {
							privateVars.touchDiff = mouseEvent.clientX - privateVars.touchStart;
							privateVars.touchDiffY = mouseEvent.clientY - privateVars.touchStartY;

							methods.handleTouchMove(privateVars.touchDiff, privateVars.touchDiffY);
						}
					});

				containerTouchIdHtml?.addEventListener('mouseup', function (event) {
					event.preventDefault();
					methods.handleTouchEnd();
					methods.setAutoplayInterval();
				});

				window.addEventListener('mouseup', function (event) {
					if (privateVars.touchStarted) {
						event.preventDefault();
						methods.handleTouchEnd();
						methods.setAutoplayInterval();
					}
				});
			}
		},


		startup: function () {

			const containerHtml = document.querySelector(publicVars.cssId.containerCls) as HTMLElement;

			const itemsHtml = document.querySelectorAll(publicVars.cssId.itemCls) as NodeListOf<Element>;
			const loaderWrapperHtml = document.querySelector(publicVars.cssId.loaderWrapperCls) as HTMLElement;
			const outerWrapperHtml = document.querySelector(publicVars.cssId.outerWrapperCls) as HTMLElement;

			privateVars.itemsSetDeviation = publicVars.itemsSetDeviation || 1;
			privateVars.itemsSetDeviation = Math.min(privateVars.itemsSetDeviation, itemsHtml.length);

			privateVars.itemsMoveBy = publicVars.itemsMoveBy || null;
			privateVars.autoplaySpeed = publicVars.autoplaySpeed || null;

			privateVars.itemsCountTillReset = publicVars.itemsCountTillReset || 1;
			privateVars.itemsCountTillReset = Math.min(privateVars.itemsCountTillReset, privateVars.itemsSetDeviation);

			let clonedItemsPrepend: Node[] = [];
			let clonedItemsAppend: Node[] = [];


			itemsHtml.forEach((item) => {
				privateVars.items.push(item.cloneNode(true));

				clonedItemsPrepend.push(item.cloneNode(true));
				clonedItemsAppend.push(item.cloneNode(true));
			});

			let clonedItemsPrependReversed = Array.from(clonedItemsPrepend).reverse();
			clonedItemsPrependReversed = clonedItemsPrependReversed.slice(0, privateVars.itemsSetDeviation);

			clonedItemsAppend = clonedItemsAppend.slice(0, privateVars.itemsSetDeviation);

			clonedItemsPrependReversed.forEach((item) => {
				containerHtml.prepend(item);
			});

			clonedItemsAppend.forEach((item) => {
				containerHtml.append(item);
			});

			const updatedItemsHtml = document.querySelectorAll(publicVars.cssId.itemCls);

			let itemsIterator = 1;
			updatedItemsHtml.forEach((item) => {

				const img = item.querySelector(publicVars.cssId.imageElemCls) as HTMLElement;

				if(img && img.dataset.src) {
					privateVars.imgPreload.set(itemsIterator, {
						path: img.dataset.src,
						preloaded: false
					});
				}

				item.classList.add(publicVars.cssId.itemCls.substring(1) + '' + itemsIterator);

				itemsIterator++;
			});

			privateVars.itemsCount =
				privateVars.items.length + clonedItemsPrependReversed.length + clonedItemsAppend.length;
			container.msg(privateVars.name, 'items count: ' + privateVars.itemsCount);
			container.msg(privateVars.name, 'items base count: ' + privateVars.items.length);

			if (privateVars.callbacks['beforeLoadEnd'] && typeof privateVars.callbacks['beforeLoadEnd'] ===
				'function') {
				privateVars.callbacks['beforeLoadEnd']();
				loaderWrapperHtml.style.display = 'none';
			}

			const { itemWidth, modifier, itemsVisible } = methods.calculateCurrentItemsState();

			privateVars.itemsVisible = itemsVisible;

			containerHtml.style.transform =
				'translate3d(' + (-(privateVars.itemsSetDeviation * itemWidth) - modifier) + 'px, 0, 0)';

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					containerHtml.classList.add(publicVars.cssId.isAnimatingCls.substring(1));
					outerWrapperHtml.classList.remove(publicVars.cssId.isLoadingCls.substring(1));
					privateVars.isAnimating = false;
				});
			});

			const { first, last } = methods.calculateCurrentItemsState();

			privateVars.firstItem = first;
			privateVars.lastItem = last;
			privateVars.modifier = modifier;
			privateVars.itemWidth = itemWidth;

			container.msg(privateVars.name, 'first item: ' + privateVars.firstItem);
			container.msg(privateVars.name, 'last item: ' + privateVars.lastItem);
			container.msg(privateVars.name, 'item width: ' + privateVars.itemWidth);

			methods.handleArrowsState();

			methods.setAutoplayInterval();
		},

		setAutoplayInterval: function () {

			if(privateVars.autoplayInterval) {
				clearInterval(privateVars.autoplayInterval);
			}

			if (privateVars.autoplaySpeed) {
				privateVars.autoplayInterval = setInterval(function () {
					methods.actionMoveSlider('right');
				}, privateVars.autoplaySpeed);
			}
		},

		clearAutoplayInterval: function () {
			if (privateVars.autoplayInterval) {
				clearInterval(privateVars.autoplayInterval);
				privateVars.autoplayInterval = null;
			}
		},

		handleTouchMove: function (diff: number, diffY: number) {

			if (Math.abs(diffY) < 100 && Math.abs(diffY) < Math.abs(diff)) {
				const containerHtml = document.querySelector(publicVars.cssId.containerCls) as HTMLElement;

				const translate3dX = -((privateVars.firstItem - 1) * privateVars.itemWidth) - privateVars.modifier;

				const newTranslate3dX = translate3dX + diff;

				containerHtml.style.transition = 'none';
				containerHtml.style.transform = 'translate3d(' + newTranslate3dX + 'px, 0, 0)';

			}

		},

		cleanupTouchValues: function () {
			privateVars.touchStart = 0;
			privateVars.touchDiff = 0;
			privateVars.touchStartY = 0;
			privateVars.touchDiffY = 0;
		},

		handleTouchEnd: function () {

			const containerHtml = document.querySelector(publicVars.cssId.containerCls) as HTMLElement;

			if (privateVars.touchStarted && !privateVars.isAnimating && containerHtml) {

				let [translate3dXStr] = methods.getTranslate3d(containerHtml);

				let translate3dX = parseInt(translate3dXStr);

				function handleTransitionEnd() {
					privateVars.isAnimating = false;

					const { first, last, itemWidth } = methods.calculateCurrentItemsState();

					const direction = privateVars.touchDiff > 0 ? 'left' : 'right';

					privateVars.firstItem = first;
					privateVars.lastItem = last;
					privateVars.itemWidth = itemWidth;

					methods.handleArrowsState();
					methods.preloadImages();
					methods.handleInfiniteItemsPosition(direction);
					methods.cleanupTouchValues();
				}

				const { itemsVisible } = methods.calculateCurrentItemsState();

				if (translate3dX >= 0) {
					privateVars.touchStarted = false;
					privateVars.isAnimating = true;

					containerHtml.classList.add(publicVars.cssId.isAnimatingCls.substring(1));

					if (translate3dX === 0) {
						handleTransitionEnd();
						return;
					}

					containerHtml.style.transition = '';
					containerHtml.style.transform = 'translate3d(0, 0, 0)';

					containerHtml.addEventListener('transitionend', function () {
						handleTransitionEnd();
					}, { once: true });

				} else if (translate3dX <= -((privateVars.itemsCount - itemsVisible) * privateVars.itemWidth) -
					privateVars.modifier) {
					privateVars.touchStarted = false;
					privateVars.isAnimating = true;

					containerHtml.classList.add(publicVars.cssId.isAnimatingCls.substring(1));

					const transformCalc: number = ((privateVars.itemsCount - itemsVisible) * privateVars.itemWidth) -
						privateVars.modifier;

					const transformString: string = 'translate3d(-' + transformCalc + 'px, 0, 0)';

					containerHtml.style.transition = '';
					containerHtml.style.transform = transformString;

					if (translate3dX === -((privateVars.itemsCount - itemsVisible) * privateVars.itemWidth) -
						privateVars.modifier) {
						handleTransitionEnd();
						return;
					}

					containerHtml.addEventListener('transitionend', function () {
						handleTransitionEnd();
					}, { once: true });
				} else if (translate3dX < 0 && translate3dX >
					-((privateVars.itemsCount - itemsVisible) * privateVars.itemWidth) - privateVars.modifier) {
					privateVars.touchStarted = false;
					privateVars.isAnimating = true;

					containerHtml.classList.add(publicVars.cssId.isAnimatingCls.substring(1));

					const translate3dX = -((privateVars.firstItem - 1) * privateVars.itemWidth) - privateVars.modifier;

					// Calculate the number of items moved based on the touch move distance
					const itemsMoved = Math.round(Math.abs(privateVars.touchDiff) / privateVars.itemWidth);

					// Calculate the new translate3dX based on the number of items moved
					let newTranslate3dX = translate3dX;
					if (privateVars.touchDiff > 0) { // If moving to the right
						newTranslate3dX += itemsMoved * privateVars.itemWidth;
					} else { // If moving to the left
						newTranslate3dX -= itemsMoved * privateVars.itemWidth;
					}

					containerHtml.style.transition = '';
					containerHtml.style.transform = 'translate3d(' + newTranslate3dX + 'px, 0, 0)';

					if (translate3dX === newTranslate3dX) {
						handleTransitionEnd();
						return;
					}

					containerHtml.addEventListener('transitionend', function () {
						handleTransitionEnd();
					}, { once: true });
				} else {
					privateVars.touchStarted = false;
					privateVars.isAnimating = false;

					methods.cleanupTouchValues();
				}

			} else {
				privateVars.touchStarted = false;
				methods.cleanupTouchValues();
			}
		},

		preloadImages: function () {
			container.msg(privateVars.name, 'preload images');

			const { first, last, itemsVisible } = methods.calculateCurrentItemsState();

			const leftPreload = first - (itemsVisible + 1) * 2;
			let leftStartItem;
			let leftRest = 0;

			if (leftPreload > 1) {
				leftStartItem = leftPreload;
			} else {
				leftStartItem = 1;
				leftRest = Math.abs(leftPreload) + 1;
			}

			function handleImagePreload(id: number) {

				const preloadImg = privateVars.imgPreload.get(id);

				if (preloadImg && !preloadImg.preloaded) {
					preloader.preloadSingleImage(privateVars.name, preloadImg.path, () => {
					});
					privateVars.imgPreload.set(id, { path: preloadImg.path, preloaded: true });

					const itemHtml = document.querySelector(publicVars.cssId.itemCls + '' + id) as HTMLElement;

					if(itemHtml) {
						const img = itemHtml.querySelector(publicVars.cssId.imageElemCls) as HTMLImageElement;
						img.src = preloadImg.path;
					}
				}
			}

			for (let i = leftStartItem; i < first; i++) {

				const currentImgPreload = privateVars.imgPreload.get(i);

				if (currentImgPreload && !currentImgPreload.preloaded) {
					handleImagePreload(i);
				}
				// console.log('left - ' + i);
			}

			if (leftRest !== 0) {
				for (let i = privateVars.itemsCount; i > (privateVars.itemsCount - leftRest) && i > 0; i--) {
					handleImagePreload(i);
					// console.log('leftRest - ' + i);
				}
			}

			const rightPreload = last + (itemsVisible + 1) * 2;
			let rightEndItem;
			let rightRest = 0;

			if (rightPreload < privateVars.itemsCount) {
				rightEndItem = rightPreload;
			} else {
				rightEndItem = privateVars.itemsCount;
				rightRest = rightPreload - privateVars.itemsCount;
			}

			for (let i = last + 1; i <= rightEndItem; i++) {
				handleImagePreload(i);
				// console.log('right - ' + i);
			}

			if (rightRest !== 0) {
				for (let i = 1; i <= rightRest; i++) {
					handleImagePreload(i);
					// console.log('rightRest - ' + i);
				}
			}

		},

		handleArrowsState: function () {
			//always active don't do anything
		},

		actionMoveSlider: function (direction: string) {

			const containerHtml = document.querySelector(publicVars.cssId.containerCls) as HTMLElement;
			containerHtml.classList.add(publicVars.cssId.isAnimatingCls.substring(1));

			const moveBy = privateVars.itemsMoveBy || privateVars.itemsVisible;

			if (privateVars.isAnimating) {
				return;
			}

			if (direction === 'left') {
				if (privateVars.firstItem > 1) {
					privateVars.firstItem = privateVars.firstItem - moveBy;
					privateVars.lastItem = privateVars.lastItem - moveBy;

				} else {
					return;
				}
			} else {
				if (privateVars.lastItem < privateVars.itemsCount) {
					privateVars.firstItem = privateVars.firstItem + moveBy;
					privateVars.lastItem = privateVars.lastItem + moveBy;
				} else {
					return;
				}
			}

			container.msg(privateVars.name, 'first item: ' + privateVars.firstItem);
			container.msg(privateVars.name, 'last item: ' + privateVars.lastItem);

			privateVars.isAnimating = true;

			const translate3dX = -((privateVars.firstItem - 1) * privateVars.itemWidth) - privateVars.modifier;

			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					containerHtml.style.transform = 'translate3d(' + translate3dX + 'px, 0, 0)';
				});
			});

			containerHtml.addEventListener('transitionend', function () {
				methods.handleInfiniteItemsPosition(direction);
				methods.clearAutoplayInterval();
				methods.setAutoplayInterval();
				methods.handleArrowsState();
				methods.preloadImages();

			}, { once: true });
		},

		handleInfiniteItemsPosition: function (direction: string) {

			const containerHtml = document.querySelector(publicVars.cssId.containerCls) as HTMLElement;

			if (privateVars.firstItem - privateVars.itemsCountTillReset < 0 && direction === 'left') {

				container.msg(privateVars.name, 'infinite recalculate left');

				let translate3dX = -(((privateVars.firstItem - 1 + privateVars.items.length) * privateVars.itemWidth)) -
					privateVars.modifier;

				containerHtml.classList.remove(publicVars.cssId.isAnimatingCls.substring(1));
				containerHtml.style.transform = 'translate3d(' + translate3dX + 'px, 0, 0)';

				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						containerHtml.classList.add(publicVars.cssId.isAnimatingCls.substring(1));
						privateVars.isAnimating = false;

						const { first, last } = methods.calculateCurrentItemsState();

						privateVars.firstItem = first;
						privateVars.lastItem = last;
					});
				});

			} else if (privateVars.lastItem + privateVars.itemsCountTillReset > privateVars.itemsCount && direction ===
				'right') {

				container.msg(privateVars.name, 'infinite recalculate right');

				let translate3dX = -(((privateVars.firstItem - 1 - privateVars.items.length) * privateVars.itemWidth)) -
					privateVars.modifier;
				containerHtml.classList.remove(publicVars.cssId.isAnimatingCls.substring(1));

				containerHtml.style.transform = 'translate3d(' + translate3dX + 'px, 0, 0)';
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						containerHtml.classList.add(publicVars.cssId.isAnimatingCls.substring(1));
						privateVars.isAnimating = false;

						const { first, last } = methods.calculateCurrentItemsState();

						privateVars.firstItem = first;
						privateVars.lastItem = last;
					});
				});
			} else {
				privateVars.isAnimating = false;
			}
		},

		calculateCurrentItemsState: function () {
			let isLastItem = false;

			const itemHtml = document.querySelector(publicVars.cssId.itemCls) as HTMLElement;
			const containerHtml = document.querySelector(publicVars.cssId.containerCls) as HTMLElement;

			if(!itemHtml || !containerHtml) {
				container.errorMsg(privateVars.name, 'Item or Container not found');
			}

			let itemWidth = itemHtml.getBoundingClientRect().width;

			const marginLeft = parseInt(
				window.getComputedStyle(itemHtml).marginLeft);
			const marginRight = parseInt(
				window.getComputedStyle(itemHtml).marginRight);
			const columnGap = window.getComputedStyle(containerHtml).columnGap as string;

			let realColumnGap = 0;

			if(columnGap.endsWith('%')) {
				const parentWidth = containerHtml.getBoundingClientRect().width;
				const percent = parseFloat(columnGap) / 100;
				realColumnGap = parentWidth * percent;
			} else {
				realColumnGap = parseInt(columnGap);
			}

			itemWidth += marginLeft + marginRight + realColumnGap;

			const containerWidth = containerHtml.getBoundingClientRect().width;

			const itemsInContainer = Math.round((containerWidth + realColumnGap) / itemWidth);

			const [translate3dXValue] = methods.getTranslate3d(containerHtml);

			const translate3dXComputedValue = methods.getTranslate3d(containerHtml, { computed: true });

			const currentFirst = Math.round(Math.abs(parseInt(translate3dXValue)) / itemWidth) + 1;

			let currentLast = currentFirst + itemsInContainer - 1;

			if (currentLast > privateVars.itemsCount) {
				currentLast = privateVars.itemsCount;
				isLastItem = true;
			}

			return {
				modifier: (marginLeft + marginRight) / 2,
				marginLeft: marginLeft,
				marginRight: marginRight,
				translate3dXValue: translate3dXValue,
				translate3dXComputedValue: translate3dXComputedValue,
				itemWidth: itemWidth,
				containerWidth: containerWidth,
				first: currentFirst,
				last: currentLast,
				itemsVisible: itemsInContainer,
				isLast: isLastItem
			};
		},

		handleWindowResize: function () {
			const { itemWidth, modifier, itemsVisible } = methods.calculateCurrentItemsState();

			privateVars.itemWidth = itemWidth;
			privateVars.modifier = modifier;
			privateVars.itemsVisible = itemsVisible;

			methods.handleArrowsState();
			methods.preloadImages();

			const translate3dX = -((privateVars.firstItem - 1) * privateVars.itemWidth) - privateVars.modifier;

			const containerHtml = document.querySelector(publicVars.cssId.containerCls) as HTMLElement;

			containerHtml.classList.remove(publicVars.cssId.isAnimatingCls.substring(1));
			containerHtml.style.transform = 'translate3d(' + translate3dX + 'px, 0, 0)';

			privateVars.isAnimating = false;
		},

		getTranslate3d: function (el: HTMLElement, config?: {
			computed: boolean
		}) {

			let transform;

			if (config && config.computed) {
				transform = window.getComputedStyle(el).transform;

				const values = transform.split(/\w+\(|\);?/);

				if (!values[1] || !values[1].length) {
					return ['0', '0', '0'];
				}

				const valuesComputed = values[1].split(/,\s?/g);

				let valueX = valuesComputed[4] || '0';
				let valueY = valuesComputed[5] || '0';

				if (valueX) {
					valueX = valueX + 'px';
				}

				if (valueY) {
					valueY = valueY + 'px';
				}

				return [valueX, valueY, '0'];

			} else {
				transform = el.style.transform;

				const values = transform.split(/\w+\(|\);?/);

				if (!values[1] || !values[1].length) {
					return ['0', '0', '0'];
				}

				return values[1].split(/,\s?/g);
			}
		},
	};

	return {
		init: function (options: PublicVarsInterface) {
			methods.init(options);
		},
		setCallback: function (name: ('beforeLoadEnd'), callback: () => void) {
			privateVars.callbacks[name] = callback;
		}
	};
};

const factory = {
	objId: 0,

	create: function () {
		factory.objId++;

		return sliderListCut(factory.objId);
	}
};

export default {
	create: factory.create
};